// components/Header/Header.jsx
import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Box,
  Avatar,
  useMediaQuery,
  useTheme as useMuiTheme,
} from '@mui/material';
import { ShoppingCart, Menu as MenuIcon, ShoppingBag, Tag } from 'lucide-react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { UserMenu } from './UserMenu';
import { MobileDrawer } from './MobileDrawer';
import { ThemeToggleButton } from './ThemeToggle';
import { useUserInitials } from './UserInfo';
import { LanguageSwitcher } from '../LanguageSwitcher/LanguageSwitcher';

export const Header = ({ onCartClick }) => {
  const { t } = useTranslation();
  const { getTotalItems } = useCart();
  const { user } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const muiTheme = useMuiTheme();

  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const cartItemCount = getTotalItems();
  const userInitials = useUserInitials();

  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const isMyyPage = location.pathname === '/myy';
  const isOstaPage = !isMyyPage; // Osta is active on all paths except /myy

  // Infer what the user was likely browsing, so we can carry it into the
  // sell flow as a suggested category (e.g. /liput -> "liput",
  // /products?c=books -> "books"). Returns null when there's no clear signal.
  const getExpectedCategory = () => {
    if (location.pathname === '/liput') {
      return 'journey-tickets';
    }
    if (location.pathname === '/products') {
      const categoryParam = searchParams.get('c');
      if (categoryParam) return categoryParam;
    }
    return null;
  };

  // Navigation handlers
  const handleOstaClick = () => {
    if (isMyyPage) {
      navigate(-1); // Go back to previous page if on /myy
    } else {
      navigate('/'); // Go to home if not on /myy
    }
  };

  const handleMyyClick = () => {
    const expectedCategory = getExpectedCategory();
    if (expectedCategory) {
      navigate(`/myy?category=${encodeURIComponent(expectedCategory)}`);
    } else {
      navigate('/myy');
    }
  };

  // Different styling for light/dark mode
  const appBarSx = {
    bgcolor: isDark ? 'background.paper' : 'primary.main',
    borderBottom: isDark ? '1px solid' : 'none',
    borderColor: 'divider',
    boxShadow: isDark
      ? 'none'
      : '0 2px 8px rgba(0, 180, 216, 0.15)',
  };

  // Icon color adapts to background
  const iconColor = isDark ? 'text.primary' : 'inherit';

  const cartBadgeSx = {
    '& .MuiBadge-badge': {
      fontFamily: 'Inter, sans-serif',
      fontWeight: 600,
      bgcolor: 'error.main',
      color: 'white',
      ...(isMobile && {
        fontSize: '0.65rem',
        minWidth: 16,
        height: 16,
      }),
    },
  };

  // Navigation items styling - Simple opacity/background for active state
  const navItemSx = (isActive) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: isMobile ? '6px' : '10px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    fontSize: isMobile ? '0.75rem' : '0.875rem',
    cursor: 'pointer',
    color: isActive ? iconColor : 'rgba(255, 255, 255, 0.5)',
    transition: 'all 0.2s ease',
    textDecoration: 'none',
    background: isActive ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
    border: 'none',
    padding: isMobile ? '8px 12px' : '12px 16px',
    borderRadius: '6px',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
      color: iconColor,
    },
  });

  // Mobile Layout
  if (isMobile) {
    // Special mobile layout for homepage
    if (isOstaPage && location.pathname === '/') {
      return (
        <>
          <AppBar position="sticky" sx={appBarSx}>
            <Toolbar sx={{ minHeight: 52, px: 1, gap: 0.5 }}>
              <IconButton
                sx={{ color: iconColor }}
                edge="start"
                onClick={() => setDrawerOpen(true)}
                size="small"
              >
                <MenuIcon size={22} />
              </IconButton>

              <Box sx={{ flexGrow: 1 }} />

              <ThemeToggleButton size={20} />

              {cartItemCount > 0 ? (
                <IconButton 
                  sx={{ color: iconColor }} 
                  onClick={onCartClick} 
                  size="small"
                >
                  <Badge badgeContent={cartItemCount} sx={cartBadgeSx}>
                    <ShoppingCart size={20} />
                  </Badge>
                </IconButton>
              ) : (
                <LanguageSwitcher />
              )}

              {user ? (
                <IconButton
                  sx={{ color: iconColor }}
                  onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                  size="small"
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: 'secondary.main',
                      fontSize: '0.75rem',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600,
                    }}
                  >
                    {userInitials}
                  </Avatar>
                </IconButton>
              ) : (
         <IconButton 
  sx={{ color: iconColor }} 
  onClick={() => navigate('/auth')}
  size="small"
>
  <Avatar
    sx={{
      width: 28,
      height: 28,
      bgcolor: isDark ? '#444' : '#ccc',
      fontSize: '0.75rem',
    }}
  />
</IconButton>
              )}
            </Toolbar>
          </AppBar>

          <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

          <UserMenu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={() => setUserMenuAnchor(null)}
          />
        </>
      );
    }

    // Default mobile layout with Osta/Myy navigation
    return (
      <>
        <AppBar position="sticky" sx={appBarSx}>
          <Toolbar sx={{ minHeight: 52, px: 1, gap: 0.5 }}>
            {/* Left: Navigation with icons */}
            <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-end' }}>
              <Box
                component="button"
                onClick={handleOstaClick}
                sx={navItemSx(isOstaPage)}
              >
                <ShoppingBag size={18} />
                {t('mode.buy')}
              </Box>
              <Box
                component="button"
                onClick={handleMyyClick}
                sx={navItemSx(isMyyPage)}
              >
                <Tag size={18} />
                {t('mode.sell')}
              </Box>
            </Box>

            <Box sx={{ flexGrow: 1 }} />

            {/* Right: Theme toggle and user icons */}
            <ThemeToggleButton size={20} />

            {user ? (
              <IconButton
                sx={{ color: iconColor }}
                onClick={(e) => setUserMenuAnchor(e.currentTarget)}
                size="small"
              >
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: 'secondary.main',
                    fontSize: '0.75rem',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  {userInitials}
                </Avatar>
              </IconButton>
            ) : (
              <IconButton sx={{ color: iconColor }}
               size="small">
                <Avatar
                  sx={{
                    width: 28,
                    height: 28,
                    bgcolor: isDark ? '#444' : '#ccc',
                    fontSize: '0.75rem',
                  }}
                />
              </IconButton>
            )}

            {cartItemCount > 0 ? (
              <IconButton 
                sx={{ color: iconColor }} 
                onClick={onCartClick} 
                size="small"
              >
                <Badge badgeContent={cartItemCount} sx={cartBadgeSx}>
                  <ShoppingCart size={20} />
                </Badge>
              </IconButton>
            ) : (
              <LanguageSwitcher />
            )}
          </Toolbar>
        </AppBar>

        <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

        <UserMenu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
        />
      </>
    );
  }

  // Desktop Layout
  return (
    <>
      <AppBar position="sticky" sx={appBarSx}>
        <Toolbar sx={{ minHeight: 64, px: 2, gap: 2 }}>
          <IconButton
            sx={{ color: iconColor }}
            edge="start"
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon size={24} />
          </IconButton>

          {/* Navigation - Icon on left, text on right */}
          <Box
            component="nav"
            sx={{
              display: 'flex',
              gap: 0,
              ml: 2,
            }}
          >
            <Box
              component="button"
              onClick={handleOstaClick}
              sx={navItemSx(isOstaPage)}
            >
              <ShoppingBag size={22} />
              {t('mode.buy')}
            </Box>
            <Box
              component="button"
              onClick={handleMyyClick}
              sx={navItemSx(isMyyPage)}
            >
              <Tag size={22} />
              {t('mode.sell')}
            </Box>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <ThemeToggleButton />

          {user ? (
            <IconButton
              sx={{ color: iconColor }}
              onClick={(e) => setUserMenuAnchor(e.currentTarget)}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'secondary.main',
                  fontSize: '0.875rem',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 600,
                }}
              >
                {userInitials}
              </Avatar>
            </IconButton>
          ) : (
            <IconButton
              onClick={() => navigate('/auth')}
               sx={{ color: iconColor }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDark ? '#444' : '#ccc',
                  fontSize: '0.875rem',
                }}
              />
            </IconButton>
          )}

          {cartItemCount > 0 ? (
            <IconButton sx={{ color: iconColor }} onClick={onCartClick}>
              <Badge badgeContent={cartItemCount} sx={cartBadgeSx}>
                <ShoppingCart size={24} />
              </Badge>
            </IconButton>
          ) : (
            <LanguageSwitcher />
          )}
        </Toolbar>
      </AppBar>

      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <UserMenu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={() => setUserMenuAnchor(null)}
      />
    </>
  );
};
